<script setup lang="ts">
import WrClock from "../atoms/icons/WrClock.vue";

type PricingCard = {
  planTypeIcon: Component;
  planLabel: string;
  billingType: string;
  priceAmount: string;
  featuresList: () => string[];
  ctaText: string;
  highlightNote: string;
};

type Props = {
  pricingContent: PricingCard[];
};

const props = withDefaults(defineProps<Props>(), {
  pricingContent: () => [
    {
      planTypeIcon: markRaw(WrClock),
      planLabel: "Hire on",
      billingType: "HOURLY BASIS",
      priceAmount: "$25",
      featuresList: () => [
        "On-Demand Model",
        "Flexibility and Scalability",
        "Focus on Core Activities",
        "Dedicated Professionals",
      ],
      ctaText: "Get started",
      highlightNote: "• Limited time offer •",
    },
  ],
});
const emit = defineEmits(["openPopup"]);

const handleContactUsPopup = () => {
  emit("openPopup");
  return;
};
</script>

<template>
  <div class="w-full flex flex-wrap justify-center gap-6">
    <div
      v-for="pricingCard in pricingContent"
      :key="pricingCard.planLabel"
      class="p-px rounded-lg bg-white bg-[image:var(--border-pricing-card)] min-w-[312px] max-w-[350px] flex-1"
    >
      <div
        class="w-full rounded-lg text-white bg-[image:var(--bg-pricing-card-bottom)] pb-12"
      >
        <div
          class="shadow-[inset_3px_3px_0px_-2px_#00000040] p-px dark:p-0 rounded-lg bg-[linear-gradient(180deg,_#7758AE_0%,_#D3CBF7_100%)] dark:bg-none mb-6"
        >
          <div
            class="h-full text-white text-center bg-[image:var(--bg-pricing-card-top)] py-6 rounded-lg dark:rounded-t-lg dark:rounded-b-none"
          >
            <div class="flex justify-center mb-2">
              <div
                class="shadow-[-2px_3px_21.4px_1px_#0000001A] dark:shadow-[-2px_3px_23.4px_1px_#00000040,_3px_4px_5.7px_0px_#00000040_inset] size-20 rounded-full border border-[#462383] bg-white dark:bg-transparent dark:bg-[image:var(--bg-pricing-card-icon)] flex items-center justify-center -mt-16"
              >
                <component :is="pricingCard.planTypeIcon" class="mx-auto" />
              </div>
            </div>
            <p class="text-sm mb-1">{{ pricingCard.planLabel }}</p>
            <div
              class="text-xl uppercase inline-block tracking-[0.5px] px-4 py-0.5 rounded-full border dark:border-[#845DBE] dark:text-[#9D78D4] mb-3 max-w-max mx-auto"
            >
              {{ pricingCard.billingType }}
            </div>
            <h2 class="tracking-[0.5px] text-4xl font-semibold">
              {{ pricingCard.priceAmount }}
            </h2>
          </div>
        </div>
        <div class="space-y-4 mb-8 max-w-[220px] mx-auto">
          <div
            v-for="feature in pricingCard.featuresList"
            :key="feature"
            class="flex items-center"
          >
            <div
              class="flex-shrink-0 p-1.5 rounded-md border border-[#B5B5B599] dark:border-[#361269] mr-3 -scroll-mt-0.5"
            >
              <div
                class="w-4 h-4 rounded-full dark:bg-[#AC8BDC] bg-[#4902A6] flex items-center justify-center"
              >
                <svg
                  class="w-3 h-3 text-[var(--foreground-invert)]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M21 7L9 19l-5.5-5.5l1.41-1.41L9 16.17L19.59 5.59z"
                  />
                </svg>
              </div>
            </div>
            <span class="text-sm text-[var(--foreground)] tracking-[0.5px]">{{
              feature
            }}</span>
          </div>
        </div>
        <WrButton container-class="mx-auto" @click="handleContactUsPopup">
          {{ pricingCard.ctaText }}
        </WrButton>
        <p class="text-xs text-center text-[#AE45AA] mt-3 tracking-[0.5px]">
          {{ pricingCard.highlightNote }}
        </p>
      </div>
    </div>
  </div>
</template>
